import { z } from "zod";
import { BrowserActionSchema, FormPlanSchema } from "./index";

export const COACHING_PROTOCOL = "contextual-coaching-v1" as const;
export const COACHING_LIMITS = { changedActions: 3, semanticCalls: 2, loadTimeoutMs: 15000 } as const;
export const CoachingScopeSchema = z.object({
  subject: z.enum(["page", "attachment", "conversation", "explicit_target"]),
  pageRelation: z.enum(["relevant", "unrelated", "uncertain"]),
  taskRelation: z.enum(["related", "unrelated", "uncertain"]),
  taskId: z.string().min(1).max(160).optional(),
  sourcePlatform: z.string().min(1).max(80).optional(),
  targetPlatform: z.string().min(1).max(80).optional(),
  reason: z.string().min(1).max(300),
}).strict();
export type CoachingScope = z.infer<typeof CoachingScopeSchema>;
export const CoachingBackgroundSchema = z.object({
  budgetCents: z.number().int().nonnegative().nullable(), weeklyHours: z.number().nonnegative().nullable(),
  goals: z.array(z.string().max(300)).max(12), constraints: z.array(z.string().max(300)).max(20),
}).strict();
export type CoachingBackground = z.infer<typeof CoachingBackgroundSchema>;

// Lazy imports permit either package entry point to load first. Unlike the legacy
// action schema, executable extras must fail rather than being silently stripped.
// ⚠️ 这里**逐个列出** BrowserAction 的变体（而不是按下标取 options[0]/[1]），
// 加新变体时必须同步；`coaching.test.ts` 有一条用例专门把两者钉在一起，
// 否则新变体会在陪跑链路上被静默挡掉。
export const CoachingActionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("navigate"), action: z.lazy(() => z.discriminatedUnion("type", [
    BrowserActionSchema.options[0].strict(), BrowserActionSchema.options[1].strict(), BrowserActionSchema.options[2].strict(),
  ])) }).strict(),
  z.object({ kind: z.literal("form"), plan: z.lazy(() => FormPlanSchema.strict()), pageUrlHash: z.string().regex(/^[a-f\d]{64}$/).optional() }).strict(),
]);
export type CoachingAction = z.infer<typeof CoachingActionSchema>;
export const CoachingPlanSchema = z.object({
  goal: z.string().min(1).max(300),
  facts: z.array(z.object({ label: z.string().min(1).max(100), value: z.string().min(1).max(600),
    source: z.enum(["page", "user", "memory", "task"]), reference: z.string().min(1).max(200) }).strict()).max(20),
  missing: z.array(z.string().min(1).max(200)).max(8),
  steps: z.array(z.object({ id: z.string().min(1).max(80), action: CoachingActionSchema,
    // A model's confirmation requirement is NEVER evidence of a user grant.
    confirmation: z.enum(["explicit_command", "plan", "fields"]),
  }).strict().refine(step => step.action.kind !== "form" || step.confirmation === "fields", "表单须逐字段确认")).min(1).max(COACHING_LIMITS.changedActions),
}).strict().refine(p => new Set(p.steps.map(s => s.id)).size === p.steps.length, "步骤 ID 重复");
export type CoachingPlan = z.infer<typeof CoachingPlanSchema>;
export const CoachingProposalSchema = z.discriminatedUnion("disposition", [
  z.object({ disposition: z.literal("answer"), message: z.string().min(1).max(8000) }).strict(),
  z.object({ disposition: z.literal("clarify"), message: z.string().min(1).max(2000) }).strict(),
  z.object({ disposition: z.literal("plan"), message: z.string().min(1).max(2000), plan: CoachingPlanSchema }).strict(),
]);
export type CoachingProposal = z.infer<typeof CoachingProposalSchema>;
const EvidenceSelectionSchema = z.object({ reference: z.string().min(1).max(200) }).strict();
// Model output is a suggestion, not an executable record. Server-owned IDs and
// page hashes are deliberately absent; the server binds them after validation.
export const CoachingModelPlanSchema = z.object({
  goal: CoachingPlanSchema.shape.goal,
  // Accept legacy outputs for frozen prompt versions, but generate reference-only
  // selections for new calls. Free-form legacy quotes still require validation.
  facts: z.array(z.union([EvidenceSelectionSchema, CoachingPlanSchema.shape.facts.element])).max(20),
  missing: CoachingPlanSchema.shape.missing,
  steps: z.array(z.union([
    z.object({ id: z.string().min(1).max(80), action: CoachingActionSchema.options[0], confirmation: z.enum(["explicit_command", "plan"]) }).strict(),
    z.object({ id: z.string().min(1).max(80), action: z.object({ kind: z.literal("form"), plan: z.lazy(() => FormPlanSchema.omit({ id: true }).strict()) }).strict(), confirmation: z.literal("fields") }).strict(),
  ])).min(1).max(COACHING_LIMITS.changedActions),
}).strict().refine(plan => new Set(plan.steps.map(step => step.id)).size === plan.steps.length, "步骤 ID 重复");
export const CoachingModelProposalSchema = z.discriminatedUnion("disposition", [
  CoachingProposalSchema.options[0], CoachingProposalSchema.options[1],
  z.object({ disposition: z.literal("plan"), message: z.string().min(1).max(2000), plan: CoachingModelPlanSchema }).strict(),
]);
/** Bind trusted server values only; model-provided execution identifiers fail parsing. */
export function bindCoachingModelProposal(raw: unknown, binding: { runId: string; pageUrlHash: string; references?: Record<string, string> }): CoachingProposal {
  const suggested = CoachingModelProposalSchema.parse(raw);
  return CoachingProposalSchema.parse(suggested.disposition !== "plan" ? suggested : {
    ...suggested, plan: { ...suggested.plan, facts: suggested.plan.facts.map(fact => {
      if ("value" in fact) return fact;
      const reference = fact.reference, source = reference.split(":")[0]!;
      if (!binding.references || !Object.hasOwn(binding.references, reference) || !binding.references[reference]
        || !["user", "page", "memory", "task"].includes(source)) throw new Error("COACHING_INVALID_REFERENCE");
      const labels: Record<string, string> = { user: "用户提供的原文", page: "页面原文", memory: "历史资料原文", task: "相关任务原文" };
      return { label: labels[source], source, reference, value: binding.references[reference]!.slice(0, 600) };
    }), steps: suggested.plan.steps.map(step => step.action.kind === "form"
      ? { ...step, action: { ...step.action, plan: { ...step.action.plan, id: `${binding.runId}:${step.id}` }, pageUrlHash: binding.pageUrlHash } } : step) },
  });
}
export const CoachingStateSchema = z.enum(["proposed", "awaiting_confirmation", "running", "verifying", "waiting_user", "completed", "paused", "failed"]);
export type CoachingState = z.infer<typeof CoachingStateSchema>;
export const CoachingObservationSchema = z.object({
  actionId: z.string().min(1).max(160),
  outcome: z.enum(["loaded", "verified", "empty", "login_required", "challenge", "failed", "unknown"]),
  evidenceId: z.string().min(1).max(160), summary: z.string().max(1200),
}).strict();
export type CoachingObservation = z.infer<typeof CoachingObservationSchema>;
export const CoachingVerificationSchema = z.object({
  result: z.enum(["reached", "blocked", "unknown"]),
  evidenceIds: z.array(z.string().min(1).max(160)).min(1).max(6),
  summary: z.string().min(1).max(1200),
}).strict();
export type CoachingVerification = z.infer<typeof CoachingVerificationSchema>;
export const CoachingViewSchema = z.object({
  id: z.string().uuid(), revision: z.number().int().positive(), state: CoachingStateSchema,
  plan: CoachingPlanSchema, observations: z.array(CoachingObservationSchema), protocol: z.literal(COACHING_PROTOCOL),
}).strict();
export type CoachingView = z.infer<typeof CoachingViewSchema>;
export function coachingOutputJsonSchema(stage: "coaching_plan" | "coaching_verify", references?: string[]) {
  const selectionProposal = z.discriminatedUnion("disposition", [CoachingProposalSchema.options[0], CoachingProposalSchema.options[1],
    CoachingModelProposalSchema.options[2].extend({ plan: CoachingModelPlanSchema.safeExtend({ facts: z.array(EvidenceSelectionSchema).max(20) }) })]);
  const schema = z.toJSONSchema(stage === "coaching_plan" ? selectionProposal : CoachingVerificationSchema);
  if (stage === "coaching_plan") {
    const requireLanguageDecision = (value: unknown) => {
      if (!value || typeof value !== "object") return;
      const node = value as Record<string, unknown>;
      const properties = node.properties as Record<string, unknown> | undefined;
      if (properties?.searchLocalization) node.required = [...new Set([...(node.required as string[] || []), "searchLocalization"])];
      Object.values(node).forEach(requireLanguageDecision);
    };
    requireLanguageDecision(schema);
  }
  if (stage === "coaching_plan" && references?.length) {
    const visit = (value: unknown) => {
      if (!value || typeof value !== "object") return;
      const node = value as Record<string, unknown>;
      const properties = node.properties as Record<string, Record<string, unknown>> | undefined;
      if (properties?.reference) properties.reference.enum = [...new Set(references)];
      Object.values(node).forEach(visit);
    };
    visit(schema);
  }
  return schema;
}
